/**
 * @file `<ChapterProgress/>` — segmented progress bar at the top of
 *       the chapter pane.
 * @module @labcraft/course-kit/components/chapter-progress
 *
 * Reads the active chapter state via {@link useChapter}; one segment
 * per step. Completed steps render solid; the active step renders the
 * half-filled gradient; upcoming steps render the muted track color.
 *
 * Counter shows "completed / total" — stays correct even if the
 * reader re-runs an already-completed step (reducer no-ops the
 * re-completion for progress counting).
 */

import type { JSX } from 'react'
import { useChapter } from '../runtime/context.tsx'
import styles from './chapter_progress.module.css'

export function ChapterProgress(): JSX.Element {
  const state = useChapter()
  const total = state.spec.objectives.length
  const done = state.completedObjectiveIds.size
  return (
    <div className={styles.progress}>
      <div className={styles.meta}>
        <span className={styles.label}>Chapter progress</span>
        <span className={styles.count}>
          <span className={styles.countCurrent}>{done}</span>
          <span className={styles.countOf}>/ {total}</span>
        </span>
      </div>
      <div
        className={styles.bar}
        role="progressbar"
        aria-valuenow={done}
        aria-valuemin={0}
        aria-valuemax={total}>
        {state.spec.objectives.map((objective, idx) => {
          const isDone = state.completedObjectiveIds.has(objective.id)
          const isActive = !isDone && state.currentObjectiveIndex === idx
          const className = [
            styles.seg,
            isDone ? styles.segDone : '',
            isActive ? styles.segActive : '',
          ]
            .filter(Boolean)
            .join(' ')
          return <span key={objective.id} className={className} />
        })}
      </div>
    </div>
  )
}
